/* Studio Manager · Lumetier waitlist
   Lists every prospect who signed up via the public Lumetier landing page.
   Newest first, with a CSV export, one-click delete, and a one-click
   "Convert to invite" that turns a waitlist applicant into a real Studio Invite. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Mail, RefreshCw, Send, CheckCircle2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow, EditorialStatusPill, EditorialAction } from "@/components/admin/EditorialList";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";

const TYPE_LABELS = {
  wedding: "Wedding",
  family: "Family / portrait",
  newborn: "Newborn",
  branding: "Branding / commercial",
  event: "Event",
  other: "Other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (err) {
    console.warn("waitlist date parse failed", err);
    return iso;
  }
}

export default function AdminWaitlist() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null); // the waitlist entry being converted

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lumetier/waitlist");
      setEntries(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load waitlist");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(entry) {
    if (!window.confirm(`Remove ${entry.email} from the waitlist?`)) return;
    try {
      await api.delete(`/admin/lumetier/waitlist/${entry.id}`);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success("Removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
    }
  }

  function exportCSV() {
    if (!entries.length) return toast.error("Nothing to export yet.");
    const rows = [
      ["Name", "Email", "Photographer type", "Source", "Signed up", "Converted"],
      ...entries.map((e) => [
        e.name || "",
        e.email || "",
        TYPE_LABELS[e.photographer_type] || e.photographer_type || "",
        e.source || "landing",
        e.created_at || "",
        e.converted_to_invite_id ? "yes" : "no",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lumetier-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Exported CSV.");
  }

  const convertedCount = entries.filter((e) => e.converted_to_invite_id).length;

  return (
    <div className="space-y-6" data-testid="admin-waitlist">
      <AdminPageHeader
        breadcrumb={["LUMETIER", "WAITLIST"]}
        title="Charter waitlist"
        count={entries.length}
        rightSlot={
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="waitlist-refresh-btn"
              className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={exportCSV}
              data-testid="waitlist-export-btn"
              className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] bg-foreground text-background hover:opacity-90"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        }
      />
      <p className="text-sm text-muted-foreground -mt-4 max-w-xl">
        Every prospect who has signed up via the public Lumetier landing page. Convert applicants to invites in one click.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="waitlist-stats">
        <Stat label="Total signups" value={entries.length} />
        <Stat label="Converted" value={convertedCount} />
        <Stat label="Pending" value={entries.length - convertedCount} />
        <Stat label="Wedding" value={entries.filter((e) => e.photographer_type === "wedding").length} />
      </div>

      {loading ? (
        <div className="py-10 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground" data-testid="waitlist-loading">Loading…</div>
      ) : entries.length === 0 ? (
        <EditorialEmptyState
          eyebrow="The list is open"
          headline={<>No signups <em>yet</em>.</>}
          description={<>Share <a href="/lumetier" className="underline">/lumetier</a> to start collecting leads.</>}
          tone="charcoal"
          data-testid="waitlist-empty"
        />
      ) : (
        <EditorialList data-testid="waitlist-rows">
          {entries.map((e, i) => (
            <EditorialRow
              key={e.id}
              index={i}
              title={e.name || "—"}
              meta={[
                <a key="email" href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                  <Mail className="w-3 h-3" /> {e.email}
                </a>,
                TYPE_LABELS[e.photographer_type] || e.photographer_type || null,
                fmtDate(e.created_at),
              ]}
              status={e.converted_to_invite_id ? (
                <EditorialStatusPill tone="sage" data-testid={`waitlist-converted-${e.id}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Converted
                </EditorialStatusPill>
              ) : null}
              actions={
                <>
                  {!e.converted_to_invite_id && (
                    <EditorialAction
                      onClick={() => setConverting(e)}
                      data-testid={`waitlist-convert-${e.id}`}
                    >
                      Convert to invite
                    </EditorialAction>
                  )}
                  <button
                    onClick={() => handleDelete(e)}
                    title="Remove"
                    data-testid={`waitlist-delete-${e.id}`}
                    className="text-muted-foreground/70 hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              }
              data-testid={`waitlist-row-${e.id}`}
            />
          ))}
        </EditorialList>
      )}

      {converting && (
        <ConvertModal
          entry={converting}
          onClose={() => setConverting(null)}
          onConverted={async () => {
            setConverting(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ConvertModal({ entry, onClose, onConverted }) {
  // Default the studio name to "{name} Photography" — the founder can rewrite it
  const [studioName, setStudioName] = useState(`${entry.name || "Studio"} Photography`);
  const [personalNote, setPersonalNote] = useState(
    `Hey ${(entry.name || "there").split(" ")[0]} — thanks for signing up. Here's your private invite to Lumetier.`,
  );
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!studioName.trim()) return toast.error("Studio name is required");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/lumetier/waitlist/${entry.id}/convert`, {
        studio_name: studioName.trim(),
        personal_note: personalNote.trim(),
      });
      toast.success(`Invite sent to ${data.owner_email}`);
      await onConverted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Conversion failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="convert-modal-backdrop"
    >
      <div
        className="bg-card border border-border max-w-lg w-full p-7"
        onClick={(e) => e.stopPropagation()}
        data-testid="convert-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Convert to invite
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="convert-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Send a one-click setup link to <strong className="text-foreground">{entry.name}</strong> at{" "}
          <a href={`mailto:${entry.email}`} className="underline">{entry.email}</a>.
        </p>

        <label className="block mb-4">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Studio name <span className="text-rose-400">*</span>
          </span>
          <input
            type="text"
            value={studioName}
            onChange={(e) => setStudioName(e.target.value)}
            data-testid="convert-studio-name"
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
          />
        </label>

        <label className="block mb-6">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Personal note (appears in the invite email)
          </span>
          <textarea
            value={personalNote}
            onChange={(e) => setPersonalNote(e.target.value)}
            rows={4}
            data-testid="convert-personal-note"
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
            data-testid="convert-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="convert-submit"
            className="inline-flex items-center gap-2 h-10 px-6 text-xs uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div
      className="border border-[color:var(--gold-foil)]/30 bg-[color:var(--charcoal)] text-[color:var(--cream)] p-5 hover:border-[color:var(--gold-foil)]/60 transition-colors min-h-[120px] flex flex-col justify-between"
      data-testid="waitlist-stat-tile"
    >
      <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">{label}</div>
      <div className="font-serif text-4xl font-light italic tabular-nums leading-none mt-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value}</div>
    </div>
  );
}
